import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Cancel,
  Schedule,
  LocalHospital,
  BeachAccess,
  PersonOff,
  Search,
  FilterList,
  Edit,
  CalendarToday,
  EventAvailable,
} from '@mui/icons-material'
import { attendanceAPI, AttendanceStatus, holidayAPI, Holiday } from '../../services/api'

interface EmployeeCalendarData {
  employee_id: number
  employee_name: string
  employee_id_number: string
  department?: string
  position?: string
  attendance_data: Record<string, string>
}

interface AllEmployeesCalendarData {
  month: number
  year: number
  total_employees: number
  employees: EmployeeCalendarData[]
  holidays?: Array<{
    date: string
    name: string
    type: string
    is_working_day: boolean
  }>
}

const EmployeeCalendarView: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))
  const [currentDate, setCurrentDate] = useState(new Date())
  const [calendarData, setCalendarData] = useState<AllEmployeesCalendarData | null>(null)
  const [filteredEmployees, setFilteredEmployees] = useState<EmployeeCalendarData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('')
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeCalendarData | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedStatus, setSelectedStatus] = useState<AttendanceStatus>(AttendanceStatus.PRESENT)
  const [notes, setNotes] = useState('')
  const [checkInTime, setCheckInTime] = useState('')
  const [checkOutTime, setCheckOutTime] = useState('')

  const month = currentDate.getMonth() + 1
  const year = currentDate.getFullYear()

  // Fetch calendar data
  useEffect(() => {
    fetchCalendarData()
  }, [month, year])

  // Apply filters
  useEffect(() => {
    if (!calendarData) return

    let filtered = [...calendarData.employees]

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(emp =>
        emp.employee_name.toLowerCase().includes(search) ||
        emp.employee_id_number.toLowerCase().includes(search) ||
        emp.department?.toLowerCase().includes(search) ||
        emp.position?.toLowerCase().includes(search)
      )
    }

    // Department filter
    if (departmentFilter) {
      filtered = filtered.filter(emp => emp.department === departmentFilter)
    }

    setFilteredEmployees(filtered)
  }, [calendarData, searchTerm, departmentFilter])

  const fetchCalendarData = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await attendanceAPI.getAllEmployeesCalendar(month, year)
      setCalendarData(data)
      setFilteredEmployees(data.employees)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch calendar data')
      console.error('Error fetching calendar data:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1))
  }

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month, 1))
  }

  const handleCellClick = (employee: EmployeeCalendarData, date: string) => {
    const clickedDate = new Date(date)
    const today = new Date()
    
    // Only allow editing for current and past dates
    if (clickedDate <= today) {
      setSelectedEmployee(employee)
      setSelectedDate(date)
      
      // Pre-fill with existing data if available
      const existingStatus = employee.attendance_data[date]
      if (existingStatus && existingStatus !== 'weekend') {
        setSelectedStatus(existingStatus as AttendanceStatus)
      } else {
        setSelectedStatus(AttendanceStatus.PRESENT)
      }
      
      setNotes('')
      setCheckInTime('')
      setCheckOutTime('')
      setEditDialogOpen(true)
    }
  }

  const handleSaveAttendance = async () => {
    if (!selectedEmployee || !selectedDate) return

    try {
      // Create or update attendance record using the bulk update API
      await attendanceAPI.bulkUpdate([{
        employee_id: selectedEmployee.employee_id,
        attendance_date: selectedDate,
        status: selectedStatus,
        hours_worked: checkInTime && checkOutTime ? calculateHoursWorked(checkInTime, checkOutTime) : undefined,
        notes: notes || undefined
      }])

      // Refresh calendar data
      await fetchCalendarData()
      setEditDialogOpen(false)
      setSelectedEmployee(null)
      setSelectedDate(null)
      setNotes('')
      setCheckInTime('')
      setCheckOutTime('')
    } catch (err: any) {
      setError(err.message || 'Failed to save attendance')
      console.error('Error saving attendance:', err)
    }
  }

  const calculateHoursWorked = (checkIn: string, checkOut: string): number => {
    if (!checkIn || !checkOut) return 0
    const [inHours, inMinutes] = checkIn.split(':').map(Number)
    const [outHours, outMinutes] = checkOut.split(':').map(Number)
    const inTime = inHours + inMinutes / 60
    const outTime = outHours + outMinutes / 60
    return Math.max(0, outTime - inTime)
  }


  const getStatusColor = (status: string) => {
    // Check if it's a holiday status (holiday_present, holiday_absent, etc.)
    if (status.startsWith('holiday_')) {
      return '#FFB74D' // Orange color for all holidays
    }
    
    switch (status) {
      case AttendanceStatus.PRESENT:
        return '#4caf50'
      case AttendanceStatus.ABSENT:
        return '#f44336'
      case AttendanceStatus.LATE:
        return '#ff9800'
      case AttendanceStatus.HALF_DAY:
        return '#2196f3'
      case AttendanceStatus.SICK_LEAVE:
        return '#e91e63'
      case AttendanceStatus.VACATION:
        return '#9c27b0'
      case AttendanceStatus.PERSONAL_LEAVE:
        return '#607d8b'
      case 'weekend':
        return '#e0e0e0'
      case 'holiday':
        return '#FFB74D' // Orange color for holidays
      default:
        return '#ffffff'
    }
  }

  const getStatusLabel = (status: string) => {
    if (status === 'weekend') return 'W'
    if (status === 'holiday') return 'H'
    
    // Handle holiday statuses (holiday_present, holiday_absent, etc.)
    if (status.startsWith('holiday_')) {
      const actualStatus = status.replace('holiday_', '')
      return actualStatus.charAt(0).toUpperCase()
    }
    
    return status.charAt(0).toUpperCase()
  }

  const getStatusIcon = (status: string) => {
    // Handle holiday statuses - extract the actual status
    let actualStatus = status
    let isHoliday = false
    
    if (status.startsWith('holiday_')) {
      isHoliday = true
      actualStatus = status.replace('holiday_', '')
    }
    
    // Get the icon based on actual status
    let icon = null
    switch (actualStatus) {
      case AttendanceStatus.PRESENT:
      case 'present':
        icon = <CheckCircle sx={{ fontSize: 14, color: isHoliday ? 'white' : 'success.main' }} />
        break
      case AttendanceStatus.ABSENT:
      case 'absent':
        icon = <Cancel sx={{ fontSize: 14, color: isHoliday ? 'white' : 'error.main' }} />
        break
      case AttendanceStatus.LATE:
      case 'late':
        icon = <Schedule sx={{ fontSize: 14, color: isHoliday ? 'white' : 'warning.main' }} />
        break
      case AttendanceStatus.HALF_DAY:
      case 'half_day':
        icon = <Schedule sx={{ fontSize: 14, color: isHoliday ? 'white' : 'info.main' }} />
        break
      case AttendanceStatus.SICK_LEAVE:
      case 'sick_leave':
        icon = <LocalHospital sx={{ fontSize: 14, color: isHoliday ? 'white' : 'error.main' }} />
        break
      case AttendanceStatus.VACATION:
      case 'vacation':
        icon = <BeachAccess sx={{ fontSize: 14, color: isHoliday ? 'white' : 'primary.main' }} />
        break
      case AttendanceStatus.PERSONAL_LEAVE:
      case 'personal_leave':
        icon = <PersonOff sx={{ fontSize: 14, color: isHoliday ? 'white' : 'secondary.main' }} />
        break
      case 'holiday':
        icon = <EventAvailable sx={{ fontSize: 14, color: '#F57C00' }} />
        break
      default:
        icon = null
    }
    
    return icon
  }

  // Get holiday name for a specific date
  const getHolidayName = (date: string): string | null => {
    if (!calendarData?.holidays) return null
    const holiday = calendarData.holidays.find(h => h.date === date)
    return holiday ? holiday.name : null
  }

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate()
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const daysInMonth = getDaysInMonth(year, month)
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1)

  // Get unique departments for filter
  const departments = calendarData
    ? Array.from(new Set(calendarData.employees.map(e => e.department).filter(Boolean)))
    : []

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f5f7fa', p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 2, sm: 3 },
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            }}
          >
            <CalendarToday sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: 'text.primary' }}>
            Employee Calendar
          </Typography>
        </Box>
        
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'white',
          borderRadius: '16px',
          p: 0.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}>
          <IconButton
            onClick={handlePreviousMonth}
            size="small"
            sx={{
              borderRadius: '12px',
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
          >
            <ChevronLeft />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              minWidth: 150,
              textAlign: 'center',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {monthNames[month - 1]} {year}
          </Typography>
          <IconButton
            onClick={handleNextMonth}
            size="small"
            sx={{
              borderRadius: '12px',
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: { xs: 2, sm: 3 }, borderRadius: '20px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 2.5 }} alignItems="center">
            <Grid item xs={12} sm={12} md={5} lg={4}>
              <TextField
                fullWidth
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                    },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Department"
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  sx={{
                    borderRadius: '16px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                    },
                  }}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList />
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredEmployees.length} of {calendarData?.total_employees || 0} employees
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Calendar View - Desktop Table / Mobile Cards */}
      {isMobile ? (
        /* Mobile Card View */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'visible' }}>
          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : filteredEmployees.length === 0 ? (
            <Card sx={{ p: 5, textAlign: 'center', borderRadius: '20px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
              <Typography variant="body2" color="text.secondary">
                No employees found
              </Typography>
            </Card>
          ) : (
            filteredEmployees.map((employee) => (
              <Card
                key={employee.employee_id}
                sx={{
                  borderRadius: '20px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                }}
              >
                {/* Employee Header */}
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    p: 2,
                    color: 'white',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {employee.employee_name}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {employee.employee_id_number}
                  </Typography>
                  {employee.department && (
                    <Chip
                      label={employee.department}
                      size="small"
                      sx={{
                        mt: 1,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontSize: '0.7rem',
                        height: 20,
                      }}
                    />
                  )}
                </Box>

                {/* Calendar Grid */}
                <CardContent sx={{ p: 2, overflow: 'auto' }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, minmax(40px, 1fr))',
                      gap: 0.5,
                      minWidth: '280px',
                    }}
                  >
                    {/* Week day headers */}
                    {weekDays.map((day) => (
                      <Box
                        key={day}
                        sx={{
                          textAlign: 'center',
                          py: 0.5,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          color: 'text.secondary',
                          minWidth: '40px',
                        }}
                      >
                        {day}
                      </Box>
                    ))}

                    {/* Empty cells for alignment */}
                    {Array.from({ length: new Date(year, month - 1, 1).getDay() }).map((_, i) => (
                      <Box key={`empty-${i}`} />
                    ))}

                    {/* Calendar days */}
                    {days.map((day) => {
                      const date = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
                      const status = employee.attendance_data[date]
                      const dateObj = new Date(date)
                      const isFuture = dateObj > new Date()
                      const isWeekend = status === 'weekend'
                      const isHoliday = status === 'holiday' || (status && status.startsWith('holiday_'))
                      const isToday = dateObj.toDateString() === new Date().toDateString()
                      const holidayName = isHoliday ? getHolidayName(date) : null
                      
                      // Extract actual attendance status for holidays
                      const actualStatus = status && status.startsWith('holiday_') ? status.replace('holiday_', '') : status
                      const displayTooltip = isHoliday && holidayName
                        ? `${holidayName} - ${actualStatus ? actualStatus.replace('_', ' ').toUpperCase() : 'ABSENT'}`
                        : status ? status.replace('_', ' ').toUpperCase() : ''

                      return (
                        <Box
                          key={day}
                          onClick={() => !isFuture && handleCellClick(employee, date)}
                          sx={{
                            minWidth: '40px',
                            minHeight: '40px',
                            aspectRatio: '1',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '8px',
                            cursor: isFuture ? 'default' : 'pointer',
                            bgcolor: status ? getStatusColor(status) : isToday ? '#e3f2fd' : 'transparent',
                            opacity: isFuture ? 0.4 : 1,
                            border: isToday ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            transition: 'all 0.2s',
                            position: 'relative',
                            '&:active': {
                              transform: isFuture ? 'none' : 'scale(0.95)',
                            },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: isToday ? 700 : 600,
                              color: (status && !isWeekend) || isHoliday ? 'white' : 'text.primary',
                            }}
                          >
                            {day}
                          </Typography>
                          {status && !isWeekend && (
                            <Tooltip title={displayTooltip} arrow>
                              <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.25,
                                mt: -0.5
                              }}>
                                <Box sx={{ transform: 'scale(0.7)' }}>
                                  {getStatusIcon(status)}
                                </Box>
                                {isHoliday && (
                                  <EventAvailable sx={{ fontSize: 8, color: 'white', opacity: 0.9 }} />
                                )}
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : (
        /* Desktop Table View */
        <Card sx={{
          borderRadius: '20px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          flex: 1,
          overflow: 'auto',
          background: 'white',
        }}>
          <TableContainer sx={{
            maxHeight: 'calc(100vh - 400px)',
          }}>
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor: 'background.default',
                      minWidth: 200,
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      p: 1.5,
                    }}
                  >
                    Employee
                  </TableCell>
                  {days.map((day) => {
                    const date = new Date(year, month - 1, day)
                    const dayOfWeek = date.getDay()
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
                    const isToday = date.toDateString() === new Date().toDateString()
                    
                    return (
                      <TableCell
                        key={day}
                        align="center"
                        sx={{
                          fontWeight: 700,
                          backgroundColor: isToday
                            ? 'primary.light'
                            : isWeekend
                              ? 'action.hover'
                              : 'background.default',
                          minWidth: 80,
                          p: 1.5,
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                            {day}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {weekDays[dayOfWeek]}
                          </Typography>
                        </Box>
                      </TableCell>
                    )
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={days.length + 1} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={days.length + 1} align="center" sx={{ py: 8 }}>
                      <Typography variant="body2" color="text.secondary">
                        No employees found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map((employee) => (
                    <TableRow key={employee.employee_id} hover>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          backgroundColor: 'background.paper',
                          zIndex: 1,
                          borderRight: '1px solid',
                          borderColor: 'divider',
                          p: 1.5,
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {employee.employee_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {employee.employee_id_number}
                          </Typography>
                          {employee.department && (
                            <Chip
                              label={employee.department}
                              size="small"
                              sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      {days.map((day) => {
                        const date = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
                        const status = employee.attendance_data[date]
                        const dateObj = new Date(date)
                        const isFuture = dateObj > new Date()
                        const isWeekend = status === 'weekend'
                        const isHoliday = status === 'holiday' || (status && status.startsWith('holiday_'))
                        const holidayName = isHoliday ? getHolidayName(date) : null
                        
                        // Extract actual attendance status for holidays
                        const actualStatus = status && status.startsWith('holiday_') ? status.replace('holiday_', '') : status
                        const displayTooltip = isHoliday && holidayName
                          ? `${holidayName} - ${actualStatus ? actualStatus.replace('_', ' ').toUpperCase() : 'ABSENT'}`
                          : status ? status.replace('_', ' ').toUpperCase() : ''
                        
                        return (
                          <TableCell
                            key={day}
                            align="center"
                            sx={{
                              p: 1.5,
                              cursor: isFuture ? 'default' : 'pointer',
                              backgroundColor: status ? getStatusColor(status) : 'transparent',
                              opacity: isFuture ? 0.5 : 1,
                              minWidth: 80,
                              height: 80,
                              border: '1px solid',
                              borderColor: 'divider',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                backgroundColor: isFuture
                                  ? undefined
                                  : status
                                    ? getStatusColor(status)
                                    : 'action.hover',
                                opacity: isFuture ? 0.5 : 0.9,
                                transform: isFuture ? 'none' : 'scale(1.05)',
                                boxShadow: isFuture ? 'none' : 2,
                                zIndex: isFuture ? 'auto' : 1,
                              },
                            }}
                            onClick={() => !isFuture && handleCellClick(employee, date)}
                          >
                            {status && !isWeekend && (
                              <Tooltip title={displayTooltip} arrow>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <Box sx={{ transform: 'scale(1.3)' }}>
                                    {getStatusIcon(status)}
                                  </Box>
                                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8rem', color: isHoliday ? 'white' : 'inherit' }}>
                                    {getStatusLabel(status)}
                                  </Typography>
                                  {isHoliday && (
                                    <EventAvailable sx={{ fontSize: 10, color: 'white', opacity: 0.8 }} />
                                  )}
                                </Box>
                              </Tooltip>
                            )}
                            {isWeekend && (
                              <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.9rem', fontWeight: 600 }}>
                                W
                              </Typography>
                            )}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Legend */}
      <Box sx={{
        mt: { xs: 2, sm: 3 },
        display: 'flex',
        flexWrap: 'wrap',
        gap: { xs: 1, sm: 1.5 },
        justifyContent: 'center',
        p: 2,
        bgcolor: 'white',
        borderRadius: '20px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
      }}>
        <Chip
          size="medium"
          icon={<CheckCircle />}
          label="Present"
          sx={{
            backgroundColor: getStatusColor(AttendanceStatus.PRESENT),
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            height: { xs: 28, sm: 32 },
            borderRadius: '12px',
          }}
        />
        <Chip
          size="medium"
          icon={<Cancel />}
          label="Absent"
          sx={{
            backgroundColor: getStatusColor(AttendanceStatus.ABSENT),
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            height: { xs: 28, sm: 32 },
            borderRadius: '12px',
          }}
        />
        <Chip
          size="medium"
          icon={<Schedule />}
          label="Late"
          sx={{
            backgroundColor: getStatusColor(AttendanceStatus.LATE),
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            height: { xs: 28, sm: 32 },
            borderRadius: '12px',
          }}
        />
        <Chip
          size="medium"
          icon={<Schedule />}
          label="Half Day"
          sx={{
            backgroundColor: getStatusColor(AttendanceStatus.HALF_DAY),
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            height: { xs: 28, sm: 32 },
            borderRadius: '12px',
          }}
        />
        <Chip
          size="medium"
          icon={<LocalHospital />}
          label="Sick"
          sx={{
            backgroundColor: getStatusColor(AttendanceStatus.SICK_LEAVE),
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            height: { xs: 28, sm: 32 },
            borderRadius: '12px',
          }}
        />
        <Chip
          size="medium"
          icon={<BeachAccess />}
          label="Vacation"
          sx={{
            backgroundColor: getStatusColor(AttendanceStatus.VACATION),
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            height: { xs: 28, sm: 32 },
            borderRadius: '12px',
          }}
        />
        <Chip
          size="medium"
          icon={<PersonOff />}
          label="Personal"
          sx={{
            backgroundColor: getStatusColor(AttendanceStatus.PERSONAL_LEAVE),
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            height: { xs: 28, sm: 32 },
            borderRadius: '12px',
          }}
        />
        <Chip
          size="medium"
          label="Weekend"
          sx={{
            backgroundColor: getStatusColor('weekend'),
            color: 'text.secondary',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            height: { xs: 28, sm: 32 },
            borderRadius: '12px',
          }}
        />
        <Chip
          size="medium"
          icon={<EventAvailable />}
          label="Holiday"
          sx={{
            backgroundColor: getStatusColor('holiday'),
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            height: { xs: 28, sm: 32 },
            borderRadius: '12px',
          }}
        />
      </Box>

      {/* Edit Attendance Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            m: { xs: 0, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit />
          Edit Attendance - {selectedDate}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Employee: {selectedEmployee?.employee_name} ({selectedEmployee?.employee_id_number})
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                label="Status"
                onChange={(e) => setSelectedStatus(e.target.value as AttendanceStatus)}
              >
                <MenuItem value={AttendanceStatus.PRESENT}>Present</MenuItem>
                <MenuItem value={AttendanceStatus.ABSENT}>Absent</MenuItem>
                <MenuItem value={AttendanceStatus.LATE}>Late</MenuItem>
                <MenuItem value={AttendanceStatus.HALF_DAY}>Half Day</MenuItem>
                <MenuItem value={AttendanceStatus.SICK_LEAVE}>Sick Leave</MenuItem>
                <MenuItem value={AttendanceStatus.VACATION}>Vacation</MenuItem>
                <MenuItem value={AttendanceStatus.PERSONAL_LEAVE}>Personal Leave</MenuItem>
              </Select>
            </FormControl>

            {selectedStatus === AttendanceStatus.PRESENT && (
              <>
                <TextField
                  label="Check-in Time"
                  type="time"
                  value={checkInTime}
                  onChange={(e) => setCheckInTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Check-out Time"
                  type="time"
                  value={checkOutTime}
                  onChange={(e) => setCheckOutTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}

            <TextField
              label="Notes"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes or reason..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: { xs: 1, sm: 1.5 } }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveAttendance} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EmployeeCalendarView
